import { db } from "./database";
import type { DRActivityEvent } from "@/types/activity-event.types";
import type { Table } from "dexie";

/**
 * Threshold of consecutive (server-confirmed) failures beyond which an event is
 * excluded from `getPushable()` — cf. `recordPushFailure` and
 * `Syncer.pushActivityEvents`. 10 distinct cycles: with Syncer's progressive
 * backoff (10s → 5 min cap), that's roughly 25 minutes of sustained failure
 * before giving up — deliberately patient, to give transient issues (a
 * revisionId not yet propagated, a brief server hiccup) plenty of room to
 * resolve on their own before we exclude the event.
 */
export const ACTIVITY_EVENT_FAILURE_THRESHOLD = 10;

/**
 * Store local de l'oplog (append-only).
 *
 * Cycle de vie d'un event (cf. `DRActivityEvent` pour le détail des 3 états) :
 *  1. `append` à la capture — `localSaved: 0, revisionId: null, synced: 0` (en mémoire
 *     uniquement, viewer).
 *  2. `remove` si l'action est annulée (undo) avant toute sauvegarde.
 *  3. `markLocallySaved` quand le brouillon local est sauvegardé (IDB) — `localSaved: 1`.
 *     Ne signifie PAS "poussé au backend" : peut arriver sur plusieurs sessions offline.
 *  4. `bindPendingToRevision` au sync serveur : SEULS les pending `localSaved: 1`
 *     du dessin reçoivent la révision créée — `markLocallySaved` et `saveDraft`
 *     sont appelés ensemble (`saveDrawing()`), donc `localSaved: 1` signifie
 *     précisément "inclus dans le `dwgDraft` qui vient d'être uploadé". Un event
 *     encore `localSaved: 0` n'a par construction pas pu faire partie de ce
 *     draft (créé après la dernière sauvegarde locale, ou en cours d'abandon via
 *     `clearUnsavedByDrawing`) et doit rester pending pour la prochaine sauvegarde
 *     locale qui l'inclura réellement.
 *  5. `getPushable` / `markSynced` au push backend (cf. `Syncer.pushActivityEvents`).
 */
class ActivityEventRepository {
  private get table(): Table<DRActivityEvent, string> {
    return db.activityEvents;
  }

  append(event: DRActivityEvent): Promise<string> {
    return this.table.put(event);
  }

  async remove(eventId: string): Promise<void> {
    await this.table.delete(eventId);
  }

  /** Events pending (non rattachés à une révision) du dessin — session courante. */
  getPendingByDrawing(drawingId: number): Promise<DRActivityEvent[]> {
    return this.table
      .where("drawingId")
      .equals(drawingId)
      .filter((e) => e.revisionId === null)
      .toArray();
  }

  /**
   * Events pending ET reflétés dans le dwgDraft actuellement stocké
   * (`localSaved: 1`) — cf. `bindPendingToRevision`. Exclut par construction les
   * events créés après la dernière sauvegarde locale (encore `localSaved: 0`),
   * qui ne sont pas dans le draft qui vient d'être uploadé.
   */
  getLocallySavedPendingByDrawing(drawingId: number): Promise<DRActivityEvent[]> {
    return this.table
      .where("drawingId")
      .equals(drawingId)
      .filter((e) => e.revisionId === null && e.localSaved === 1)
      .toArray();
  }

  /**
   * Au save local (`saveDrawing()` → IDB) : marque les events pending du dessin
   * comme inclus dans le brouillon sauvegardé. N'implique PAS un push backend —
   * seulement que ces events survivraient à une fermeture d'onglet/rechargement.
   *
   * Lecture ET écriture dans la MÊME transaction (contrairement à un
   * `getPendingByDrawing` suivi d'un `db.transaction` séparé) : sinon, l'`await`
   * entre les deux laisse une fenêtre où `clearUnsavedByDrawing` (déclenché par
   * un `discardUnsavedChanges()` utilisateur à tout moment) peut s'intercaler
   * sur une lecture déjà périmée — un event `localSaved: 0` que l'utilisateur
   * vient d'abandonner pourrait alors être marqué `localSaved: 1` (ou rattaché
   * à une révision, cf. `bindPendingToRevision`) APRÈS que le filtre de
   * `clearUnsavedByDrawing` l'a jugé absent, et donc survivre — puis être
   * poussé au backend malgré l'abandon. Dexie sérialise les transactions r/w
   * qui se chevauchent sur la même table : en lisant et écrivant d'un bloc, on
   * garantit qu'un `clearUnsavedByDrawing` concurrent voit soit l'état d'avant
   * (event encore pending, donc supprimable), soit l'état d'après (déjà
   * marqué/rattaché, donc plus dans son périmètre) — jamais un entre-deux.
   */
  async markLocallySaved(drawingId: number): Promise<void> {
    await db.transaction("rw", this.table, async () => {
      const pending = await this.getPendingByDrawing(drawingId);
      for (const e of pending) await this.table.update(e.eventId, { localSaved: 1 });
    });
  }

  /**
   * Au save : rattache à la révision créée les pending qui étaient réellement
   * dans le draft uploadé (`localSaved: 1` — cf. `getLocallySavedPendingByDrawing`).
   * Les events encore `localSaved: 0` (édités après la dernière sauvegarde
   * locale, ou en cours d'abandon via `discardUnsavedChanges`) restent pending
   * : ils ne sont pas dans ce draft et seront rattachés à une révision
   * ultérieure une fois réellement sauvegardés localement. @returns les eventId
   * bindés. Lecture + écriture dans la même transaction — cf. `markLocallySaved`.
   */
  async bindPendingToRevision(
    drawingId: number,
    revisionId: number
  ): Promise<string[]> {
    return db.transaction("rw", this.table, async () => {
      const pending = await this.getLocallySavedPendingByDrawing(drawingId);
      const ids = pending.map((e) => e.eventId);
      for (const id of ids) await this.table.update(id, { revisionId });
      return ids;
    });
  }

  /**
   * Events to push: bound to a revision, not yet synced, AND not permanently
   * excluded after repeated failures (cf. ACTIVITY_EVENT_FAILURE_THRESHOLD).
   */
  getPushable(): Promise<DRActivityEvent[]> {
    return this.table
      .where("synced")
      .equals(0)
      .filter((e) => e.revisionId !== null && e.excluded !== 1)
      .toArray();
  }

  /** Marks events as synced and clears their failure history (a success resets
   *  the counter, cf. Syncer.pushActivityEvents). */
  async markSynced(eventIds: string[]): Promise<void> {
    await db.transaction("rw", this.table, async () => {
      for (const id of eventIds) {
        await this.table.update(id, { synced: 1, failureCount: 0, excluded: 0 });
      }
    });
  }

  /**
   * Isolates a persistently failing event (cf. Syncer.pushActivityEvents): call
   * ONLY after a rejection CONFIRMED by the server for THIS event taken
   * individually (never on a network/offline error, which says nothing about its
   * validity). Increments its consecutive failure count and, at the threshold,
   * excludes it from `getPushable()` — WITHOUT ever deleting it (manual
   * investigation remains possible afterwards).
   */
  async recordPushFailure(
    eventId: string
  ): Promise<{ failureCount: number; excluded: boolean }> {
    return db.transaction("rw", this.table, async () => {
      const event = await this.table.get(eventId);
      if (!event) return { failureCount: 0, excluded: false };
      const failureCount = (event.failureCount ?? 0) + 1;
      const excluded = failureCount >= ACTIVITY_EVENT_FAILURE_THRESHOLD;
      await this.table.update(eventId, {
        failureCount,
        excluded: excluded ? 1 : 0,
      });
      return { failureCount, excluded };
    });
  }

  /** Tous les events d'un dessin (pour le fil — C5). */
  getByDrawing(drawingId: number): Promise<DRActivityEvent[]> {
    return this.table.where("drawingId").equals(drawingId).toArray();
  }

  /**
   * Remplace le cache local des events déjà synchronisés d'un dessin par la lignée
   * fraîchement téléchargée (`makeOplogLocal`). Purge d'abord les `synced === 1`
   * existants — nécessaire au changement de branche pour ne pas garder les events
   * d'une ancienne lignée (sibling branch). Ne touche JAMAIS les `synced === 0` :
   * ce sont potentiellement des events locaux pas encore confirmés par le backend.
   */
  async replaceSyncedByDrawing(
    drawingId: number,
    events: DRActivityEvent[]
  ): Promise<void> {
    await db.transaction("rw", this.table, async () => {
      await this.table
        .where("drawingId")
        .equals(drawingId)
        .filter((e) => e.revisionId !== null && e.synced === 1)
        .delete();
      await this.table.bulkPut(events);
    });
  }

  /** Réattribue le drawingId (dessin neuf : tempId négatif → id serveur). */
  async remapDrawingId(oldDrawingId: number, newDrawingId: number): Promise<void> {
    await this.table
      .where("drawingId")
      .equals(oldDrawingId)
      .modify({ drawingId: newDrawingId });
  }

  /**
   * Ids des dessins portant au moins un event jamais confirmé par le backend.
   *
   * Garde de la politique de rétention locale : `deleteManyById` efface l'oplog du
   * dessin avec sa ligne, ces events seraient donc perdus. Les `excluded === 1`
   * (abandonnés après échecs répétés, cf. `recordPushFailure`) sont volontairement
   * inclus : ils restent une trace destinée à l'investigation manuelle, pas un
   * déchet à détruire silencieusement.
   */
  async getDrawingIdsWithUnsyncedEvents(): Promise<Set<number>> {
    const drawingIds = new Set<number>();
    await this.table
      .where("synced")
      .equals(0)
      .each((event) => {
        drawingIds.add(event.drawingId);
      });
    return drawingIds;
  }

  /** Vide l'oplog local d'un dessin (archivage / libération IDB). */
  async clearByDrawing(drawingId: number): Promise<void> {
    await this.table.where("drawingId").equals(drawingId).delete();
  }

  /**
   * Purge les events JAMAIS sauvegardés, ni localement ni côté backend
   * (`localSaved === 0 && revisionId === null`) — à appeler quand l'utilisateur
   * abandonne les modifications de la session courante (cf. `discardUnsavedChanges`).
   *
   * Ne touche PAS les events `localSaved === 1` : ils font partie du dernier brouillon
   * local sauvegardé (IDB) et seront légitimement rattachés à une révision au prochain
   * sync — même si `revisionId` est encore `null` à cet instant. Confondre "pending"
   * (pas encore poussé au backend) avec "jamais sauvegardé" effacerait par erreur des
   * events décrivant du contenu réellement persisté localement.
   */
  async clearUnsavedByDrawing(drawingId: number): Promise<void> {
    await this.table
      .where("drawingId")
      .equals(drawingId)
      .filter((e) => e.revisionId === null && e.localSaved === 0)
      .delete();
  }
}

export const activityEventRepository = new ActivityEventRepository();
